/**
 * Product Visualizer
 * 
 * Main entry point that orchestrates all components:
 * - SceneManager for Three.js rendering
 * - ModelLoader, TextureLoader, HDRILoader for assets
 * - MaterialManager, ToggleManager, PreviewManager for customization
 * - LightingManager, SelectionManager, ViewSnapManager, BackgroundManager, FocusLockManager
 * - GamifySdkBridge for Gamify Toolkit integration
 * - UIController for user interface
 */

import * as THREE from 'three';
import { SceneManager } from './core';
import { ModelLoader, TextureLoader, HDRILoader, type LoadedModel } from './loaders';
import { 
  MaterialManager, 
  ToggleManager, 
  PreviewManager,
  LightingManager,
  SelectionManager,
  ViewSnapManager,
  BackgroundManager,
  FocusLockManager,
} from './managers';
import { GamifySdkBridge } from './sdk';
import { UIController } from './ui';
import { ResourceTracker } from './utils';
import type { 
  VisualizerConfig, 
  GamifyVisualizerPayload,
  CustomizationState,
  VisualizerSettings,
  VisualizerTheme
} from './types';

export interface ProductVisualizerOptions {
  /** Container element or selector */
  container: HTMLElement | string;
  
  /** Enable debug logging */
  debug?: boolean;
  
  /** Timeout for SDK initialization (ms) */
  initTimeout?: number;
  
  /** Pixel ratio override (defaults to device pixel ratio, capped at 2) */
  pixelRatio?: number;
  
  /** Skip SDK initialization (for standalone testing) */
  standalone?: boolean;
  
  /** Standalone config (required if standalone=true) */
  standaloneConfig?: GamifyVisualizerPayload;
}

export class ProductVisualizer {
  // Core components
  private sceneManager: SceneManager | null = null;
  private modelLoader: ModelLoader;
  private textureLoader: TextureLoader;
  private hdriLoader: HDRILoader;
  
  // Managers
  private materialManager: MaterialManager;
  private toggleManager: ToggleManager;
  private previewManager: PreviewManager;
  private lightingManager: LightingManager;
  private selectionManager: SelectionManager;
  private viewSnapManager: ViewSnapManager;
  private backgroundManager: BackgroundManager;
  private focusLockManager: FocusLockManager;
  
  // Integration
  private sdkBridge: GamifySdkBridge;
  private uiController: UIController | null = null;
  
  // Resources
  private resourceTracker = new ResourceTracker();
  private productModel: LoadedModel | null = null;
  
  // State
  private container: HTMLElement;
  private config: VisualizerConfig | null = null;
  private initialized = false;
  private options: ProductVisualizerOptions;
  
  constructor(options: ProductVisualizerOptions) {
    this.options = options;
    
    // Resolve container
    if (typeof options.container === 'string') {
      const el = document.querySelector(options.container);
      if (!el) {
        throw new Error(`Container not found: ${options.container}`);
      }
      this.container = el as HTMLElement;
    } else {
      this.container = options.container;
    }
    
    // Ensure container has position for overlay
    const containerStyle = getComputedStyle(this.container);
    if (containerStyle.position === 'static') {
      this.container.style.position = 'relative';
    }
    
    // Initialize loaders
    this.modelLoader = new ModelLoader();
    this.textureLoader = new TextureLoader();
    this.hdriLoader = new HDRILoader();
    
    // Initialize managers
    this.materialManager = new MaterialManager(this.textureLoader);
    this.toggleManager = new ToggleManager();
    this.previewManager = new PreviewManager(new ModelLoader());
    this.lightingManager = new LightingManager();
    this.selectionManager = new SelectionManager();
    this.viewSnapManager = new ViewSnapManager();
    this.backgroundManager = new BackgroundManager();
    this.focusLockManager = new FocusLockManager();
    
    // Initialize SDK bridge
    this.sdkBridge = new GamifySdkBridge({
      debug: options.debug,
      initTimeout: options.initTimeout,
    });
  }
  
  /**
   * Initialize the visualizer
   */
  public async init(): Promise<void> {
    const startTime = performance.now();
    
    try {
      // Get config from SDK or standalone
      let payload: GamifyVisualizerPayload;
      
      if (this.options.standalone) {
        payload = this.options.standaloneConfig ?? await this.loadStandalonePayload();
      } else {
        // Wait for config from parent via postMessage
        try {
          payload = await this.sdkBridge.init();
        } catch (error) {
          this.log('SDK init failed, falling back to standalone config.', error);
          payload = this.options.standaloneConfig ?? await this.loadStandalonePayload();
        }
      }
      
      this.config = payload.config;
      
      // Determine UI visibility from config and settings
      const uiConfig = payload.config.ui ?? {};
      const settings = payload.settings;
      
      // Create UI controller
      this.uiController = new UIController({
        container: this.container,
        theme: payload.theme,
        showMaterialPanel: (uiConfig.showMaterialPanel !== false) && 
          !settings.hideMaterialPanel && 
          payload.config.materialSlots.length > 0,
        showTogglePanel: (uiConfig.showTogglePanel !== false) && 
          !settings.hideTogglePanel && 
          payload.config.toggleElements.length > 0,
        showPreviewPanel: (uiConfig.showPreviewPanel !== false) && 
          !settings.hidePreviewPanel && 
          (payload.config.previewScenes?.length ?? 0) > 0,
        showKeybinds: uiConfig.showKeybinds ?? settings.showKeybinds ?? false,
        showViewSnaps: uiConfig.showViewSnaps ?? settings.showViewSnaps ?? false,
        showFocusLock: uiConfig.showFocusLock ?? settings.showFocusLock ?? false,
        showFullscreenButton: uiConfig.showFullscreenButton ?? settings.showFullscreenButton ?? true,
        showResetButton: uiConfig.showResetButton ?? true,
      });
      
      // Show loading state
      this.uiController.showLoading('Initializing...', 0);
      
      // Create scene manager
      this.sceneManager = new SceneManager({
        container: this.container,
        cameraDefaults: payload.config.cameraDefaults,
        controlsConfig: payload.config.controlsConfig,
        theme: payload.theme,
        pixelRatio: this.options.pixelRatio,
      });
      
      // Initialize HDRI loader with renderer
      this.hdriLoader.initPMREM(this.sceneManager.renderer);
      
      // Initialize background manager
      this.backgroundManager.setRenderer(this.sceneManager.renderer);
      await this.backgroundManager.init(this.sceneManager.scene, {
        background: payload.config.background,
        backgroundColor: payload.config.backgroundColor,
        backgroundUrl: payload.config.backgroundUrl,
      });
      
      // Load environment map if specified
      if (payload.config.hdriUrl) {
        this.uiController.showLoading('Loading environment...', 0.1);
        try {
          const hdri = await this.hdriLoader.load(payload.config.hdriUrl);
          this.sceneManager.setEnvironment(hdri.envMap);
          
          // Use as background if configured or no custom background set
          const bgConfig = payload.config.background;
          if (bgConfig?.type === 'hdri' && bgConfig.showAsBackground !== false) {
            this.backgroundManager.setHdriBackground(hdri.backgroundTexture);
          } else if (!bgConfig && !payload.config.backgroundUrl && !payload.config.backgroundColor) {
            this.sceneManager.setBackground(hdri.backgroundTexture);
          }
        } catch (error) {
          console.warn('Failed to load HDRI, using default environment', error);
          this.createDefaultEnvironment();
        }
      } else {
        this.createDefaultEnvironment();
      }
      
      // Set custom background if specified
      if (payload.config.backgroundColor) {
        this.sceneManager.setBackground(new THREE.Color(payload.config.backgroundColor));
      }
      
      // Load main product model
      this.uiController.showLoading('Loading model...', 0.3);
      this.productModel = await this.modelLoader.load(
        payload.config.modelUrl,
        (progress) => {
          this.uiController?.showLoading('Loading model...', 0.3 + progress * 0.4);
        }
      );
      
      // Center and add to scene
      ModelLoader.centerModel(this.productModel.scene, this.productModel.bounds);
      this.sceneManager.scene.add(this.productModel.scene);
      this.resourceTracker.trackAll(this.productModel.scene);
      
      // Initialize lighting manager
      await this.lightingManager.init(this.sceneManager.scene, {
        lighting: payload.config.lighting,
      });
      
      // Initialize view snap manager
      await this.viewSnapManager.init(this.sceneManager.scene, {
        viewPresets: payload.config.viewPresets,
        cameraDefaults: payload.config.cameraDefaults,
      });
      this.viewSnapManager.setupCamera(
        this.sceneManager.camera,
        this.sceneManager.controls,
        () => this.sceneManager?.requestRender(),
        (presetId) => this.uiController?.updateActiveView(presetId)
      );
      
      // Initialize selection manager
      await this.selectionManager.init(this.sceneManager.scene, {
        selection: payload.config.selection,
      });
      if (payload.config.selection?.enabled) {
        this.selectionManager.setupInteraction(
          this.sceneManager.camera,
          this.sceneManager.renderer.domElement
        );
        this.selectionManager.setCallbacks({
          onHover: (_mesh, partName) => {
            this.sdkBridge.recordEvent('selection:hover', { partName });
          },
          onSelect: (_mesh, partName) => {
            this.sdkBridge.recordEvent('selection:select', { partName });
            this.sceneManager?.requestRender();
          },
        });
      }
      
      // Initialize customization managers
      this.uiController.showLoading('Setting up customization...', 0.8);
      
      await this.materialManager.init(this.sceneManager.scene, {
        slots: payload.config.materialSlots,
      });
      
      // Create pattern-based material panel from auto-detected slots
      const patternConfigs = this.materialManager.getPatternConfigs();
      if (patternConfigs.length > 0) {
        this.uiController.createPatternMaterialPanel(patternConfigs);
      }
      
      await this.toggleManager.init(this.sceneManager.scene, {
        elements: payload.config.toggleElements,
      });
      
      // Always initialize preview manager with product model
      await this.previewManager.init(this.sceneManager.scene, {
        previewScenes: payload.config.previewScenes ?? [],
        productModel: this.productModel.scene,
      });
      
      // Initialize UI
      this.uiController.init({
        materialSlots: payload.config.materialSlots,
        toggleElements: payload.config.toggleElements,
        previewScenes: payload.config.previewScenes,
        viewPresets: this.viewSnapManager.getPresets(),
      });

      // Initialize Background Panel (Custom Requirement)
      const hdriOptions = [
        { name: 'Studio', id: 'studio', url: 'https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/studio_small_08_1k.hdr' },
        { name: 'Outdoor', id: 'outdoor', url: 'https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/puresky_1k.hdr' },
        { name: 'Interior', id: 'interior', url: 'https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/brown_photostudio_02_1k.hdr' }
      ];

      this.uiController.createBackgroundPanel({
        hdriOptions: hdriOptions,
        onHdriChange: async (url) => {
          this.uiController?.showLoading('Loading environment...', 0);
          try {
            const hdri = await this.hdriLoader.load(url);
            this.sceneManager?.setEnvironment(hdri.envMap);
            // Force background update if using HDRI background
            this.backgroundManager.setHdriBackground(hdri.backgroundTexture);
            this.sceneManager?.requestRender();
          } catch (e) {
            console.error('Failed to change HDRI', e);
          } finally {
            this.uiController?.hideLoading();
          }
        },
        onSceneryChange: (visible) => {
          // Toggle first available preview scene
          const sceneId = visible && payload.config.previewScenes?.length 
            ? payload.config.previewScenes[0].id 
            : null;
          this.previewManager.setActiveScene(sceneId);
          this.sceneManager?.requestRender();
        }
      });
      
      // Initialize focus lock manager
      await this.focusLockManager.init(this.sceneManager.scene, {});
      this.focusLockManager.setup(
        this.sceneManager.camera,
        this.sceneManager.controls,
        this.sceneManager.renderer,
        () => this.sceneManager?.requestRender()
      );
      this.focusLockManager.setCallbacks({
        onModeChange: (active) => {
          this.uiController?.updateFocusLockState(active);
        },
        onZoomComplete: () => {
          this.sdkBridge.recordEvent('focusLock:zoom', {});
        },
      });
      
      // Wire up callbacks
      this.setupCallbacks();
      
      // Setup keyboard handlers
      this.setupKeyboardHandlers();
      
      // Setup mouse handlers (Ctrl+click snap, etc.)
      this.setupMouseHandlers();
      
      // Setup SDK message handlers
      this.setupSdkHandlers();
      
      // Start animation loop for managers
      this.sceneManager.addAnimationCallback((delta) => {
        this.viewSnapManager.update(delta);
        this.selectionManager.update(delta);
        this.lightingManager.update(delta);
        this.focusLockManager.update(delta);
      });
      
      // Hide loading
      this.uiController.hideLoading();
      
      // Mark as initialized
      this.initialized = true;
      
      // Request initial render
      this.sceneManager.requestRender();
      
      // Emit ready event
      const loadTime = performance.now() - startTime;
      this.sdkBridge.recordEvent('ready', { loadTime });
      
      this.log(`Visualizer initialized in ${loadTime.toFixed(0)}ms`);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.uiController?.showError(`Failed to initialize: ${errorMessage}`);
      this.sdkBridge.reportError('INIT_FAILED', errorMessage);
      throw error;
    }
  }
  
  /**
   * Create default environment lighting
   */
  private createDefaultEnvironment(): void {
    if (!this.sceneManager) return;
    
    const topColor = new THREE.Color(0xffffff);
    const bottomColor = new THREE.Color(0xcccccc);
    const envMap = this.hdriLoader.createGradientEnvironment(
      topColor,
      bottomColor,
      this.sceneManager.renderer
    );
    this.sceneManager.setEnvironment(envMap);
  }

  /**
   * Load standalone config from public assets
   */
  private async loadStandalonePayload(): Promise<GamifyVisualizerPayload> {
    const response = await fetch('/assets/demo/config.json');
    if (!response.ok) {
      throw new Error(`Failed to load standalone config: ${response.status} ${response.statusText}`);
    }
    const config = (await response.json()) as VisualizerConfig;

    const defaultTheme: VisualizerTheme = {
      primaryColor: '#0d6efd',
      secondaryColor: '#6c757d',
      backgroundColor: '#ffffff',
      textColor: '#1f2937',
      accentColor: '#22c55e',
    };

    const defaultSettings: VisualizerSettings = {
      autoRotate: false,
      autoRotateSpeed: 2,
      enableZoom: true,
      enablePan: true,
      showControls: true,
      showFullscreenButton: true,
      showKeybinds: true,
      showViewSnaps: true,
      showFocusLock: true,
      enableSelection: false,
    };

    return {
      configId: 'standalone',
      shop: 'standalone',
      config,
      theme: defaultTheme,
      settings: defaultSettings,
    };
  }
  
  /**
   * Set up keyboard handlers
   */
  private setupKeyboardHandlers(): void {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if target is an input field
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }
      
      // View snap shortcuts (1-6)
      if (this.viewSnapManager.handleKeyDown(event)) {
        event.preventDefault();
        return;
      }
      
      // F for focus on model
      if (event.key.toLowerCase() === 'f' && this.productModel) {
        this.viewSnapManager.focusOnObject(this.productModel.scene);
        event.preventDefault();
        return;
      }
      
      // Escape to clear selection
      if (event.key === 'Escape') {
        this.selectionManager.clearSelection();
        // Also deactivate focus lock mode
        if (this.focusLockManager.isActiveMode()) {
          this.focusLockManager.deactivate();
        }
        event.preventDefault();
        return;
      }
      
      // B for box-select zoom (focus lock)
      if (event.key.toLowerCase() === 'b') {
        this.focusLockManager.toggle();
        this.uiController?.updateFocusLockState(this.focusLockManager.isActiveMode());
        this.sdkBridge.recordEvent('focusLock:toggle', { 
          active: this.focusLockManager.isActiveMode() 
        });
        event.preventDefault();
        return;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    // Store for cleanup
    (this as any)._keydownHandler = handleKeyDown;
  }
  
  /**
   * Set up mouse handlers for canvas interactions
   */
  private setupMouseHandlers(): void {
    if (!this.sceneManager) return;
    
    // Ctrl+drag to snap to flat view in drag direction
    let isDragging = false;
    let startX = 0;
    let startY = 0;
    const DRAG_THRESHOLD = 20; // Minimum pixels to trigger snap
    
    const handleMouseDown = (event: MouseEvent) => {
      // Check if Ctrl key is held (Cmd on Mac)
      if (event.ctrlKey || event.metaKey) {
        isDragging = true;
        startX = event.clientX;
        startY = event.clientY;
        
        // Disable OrbitControls to prevent panning
        this.sceneManager!.controls.enabled = false;
        
        event.preventDefault();
      }
    };
    
    const handleMouseMove = (event: MouseEvent) => {
      if (!isDragging) return;
      event.preventDefault();
    };
    
    const handleMouseUp = (event: MouseEvent) => {
      if (!isDragging) return;
      isDragging = false;
      
      // Re-enable OrbitControls
      this.sceneManager!.controls.enabled = true;
      
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      // Only snap if dragged beyond threshold
      if (distance >= DRAG_THRESHOLD) {
        // Pass drag direction to snap method
        this.viewSnapManager.snapInDragDirection(deltaX, deltaY, true);
        this.sdkBridge.recordEvent('view:ctrlClickSnap', { deltaX, deltaY });
      }
    };
    
    // Use capture phase to intercept before OrbitControls
    this.sceneManager.addCanvasEventListener('mousedown', handleMouseDown, true);
    this.sceneManager.addCanvasEventListener('mousemove', handleMouseMove, true);
    this.sceneManager.addCanvasEventListener('mouseup', handleMouseUp, true);
    this.sceneManager.addCanvasEventListener('mouseleave', handleMouseUp, true);
    
    // Store for cleanup
    (this as any)._mouseHandlers = { handleMouseDown, handleMouseMove, handleMouseUp };
  }
  
  /**
   * Set up UI and manager callbacks
   */
  private setupCallbacks(): void {
    if (!this.uiController || !this.sceneManager) return;
    
    // Material changes
    this.uiController.setOnMaterialChange(async (slotId, optionId) => {
      await this.materialManager.selectOption(slotId, optionId);
      this.sceneManager?.requestRender();
      this.sdkBridge.recordEvent('material:changed', { slotId, optionId });
    });
    
    // Toggle changes
    this.uiController.setOnToggleChange((elementId: string, visible: boolean) => {
      this.toggleManager.setVisibility(elementId, visible);
      this.sceneManager?.requestRender();
      this.sdkBridge.recordEvent('toggle:changed', { elementId, visible });
    });
    
    // Preview changes
    this.uiController.setOnPreviewChange(async (sceneId: string) => {
      await this.previewManager.setActiveScene(sceneId);
      this.sceneManager?.requestRender();
      this.sdkBridge.recordEvent('preview:changed', { sceneId });
    });
    
    // Reset
    this.uiController.setOnReset(() => {
      this.reset();
    });
    
    // Fullscreen
    this.uiController.setOnFullscreen(() => {
      this.toggleFullscreen();
    });
    
    // View snap
    this.uiController.setOnViewSnap((presetId) => {
      this.viewSnapManager.snapToView(presetId);
      this.sdkBridge.recordEvent('view:snap', { presetId });
    });
    
    // Focus lock
    this.uiController.setOnFocusLock(() => {
      this.focusLockManager.toggle();
      this.sdkBridge.recordEvent('focusLock:toggle', { 
        active: this.focusLockManager.isActiveMode() 
      });
    });
    
    // Track camera changes on interaction
    this.sceneManager.controls.addEventListener('start', () => {
      this.sdkBridge.recordEvent('interaction', { type: 'start' });
    });
    
    // Preview manager camera changes
    this.previewManager.onCameraChange((defaults) => {
      this.sceneManager?.resetCamera(defaults);
    });
  }
  
  /**
   * Set up SDK message handlers for external control
   */
  private setupSdkHandlers(): void {
    // Handle material change from parent
    this.sdkBridge.on<{ slotId: string; optionId: string }>(
      'gamify:set-material',
      async ({ slotId, optionId }) => {
        await this.materialManager.selectOption(slotId, optionId);
        this.uiController?.updateMaterialSelection(slotId, optionId);
        this.sceneManager?.requestRender();
      }
    );
    
    // Handle toggle change from parent
    this.sdkBridge.on<{ elementId: string; visible: boolean }>(
      'gamify:set-toggle',
      ({ elementId, visible }) => {
        this.toggleManager.setVisibility(elementId, visible);
        this.uiController?.updateToggle(elementId, visible);
        this.sceneManager?.requestRender();
      }
    );
    
    // Handle preview change from parent
    this.sdkBridge.on<{ sceneId: string | null }>(
      'gamify:set-preview',
      async ({ sceneId }) => {
        await this.previewManager.setActiveScene(sceneId);
        this.uiController?.updatePreviewSelection(sceneId);
        this.sceneManager?.requestRender();
      }
    );
    
    // Handle reset from parent
    this.sdkBridge.on('gamify:reset', () => {
      this.reset();
    });
    
    // Handle screenshot request from parent
    this.sdkBridge.on('gamify:capture-screenshot', () => {
      if (this.sceneManager) {
        const dataUrl = this.sceneManager.captureScreenshot();
        this.sdkBridge.sendScreenshot(dataUrl);
      }
    });
  }
  
  /**
   * Reset to default state
   */
  public reset(): void {
    if (!this.config) return;
    
    this.materialManager.resetToDefaults();
    this.toggleManager.resetToDefaults();
    this.previewManager.setActiveScene(null);
    this.sceneManager?.resetCamera(this.config.cameraDefaults);
    
    // Update UI
    for (const slot of this.config.materialSlots) {
      const defaultId = slot.defaultOptionId ?? slot.options[0]?.id;
      if (defaultId) {
        this.uiController?.updateMaterialSelection(slot.id, defaultId);
      }
    }
    for (const element of this.config.toggleElements) {
      this.uiController?.updateToggle(element.id, element.defaultVisible);
    }
    this.uiController?.updatePreviewSelection(null);
    
    this.sceneManager?.requestRender();
    this.log('Reset to defaults');
  }
  
  /**
   * Toggle fullscreen mode
   */
  public toggleFullscreen(): void {
    if (!document.fullscreenElement) {
      this.container.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }
  
  /**
   * Get current customization state
   */
  public getCustomizationState(): CustomizationState {
    const materials: Record<string, string> = {};
    const toggles: Record<string, boolean> = {};
    
    for (const state of this.materialManager.getState()) {
      materials[state.slotId] = state.selectedOptionId;
    }
    
    for (const state of this.toggleManager.getState()) {
      toggles[state.elementId] = state.visible;
    }
    
    return {
      materials,
      toggles,
      previewSceneId: this.previewManager.getActiveSceneId(),
      camera: this.sceneManager?.getCameraState() ?? { position: [0, 0, 5], target: [0, 0, 0] },
    };
  }
  
  /**
   * Save current customization
   */
  public saveCustomization(): void {
    const state = this.getCustomizationState();
    this.sdkBridge.saveCustomization(state);
    this.sdkBridge.recordEvent('customization:complete', { state });
  }
  
  /**
   * Capture screenshot
   */
  public captureScreenshot(width?: number, height?: number): string | null {
    return this.sceneManager?.captureScreenshot(width, height) ?? null;
  }
  
  /**
   * Set auto-rotate
   */
  public setAutoRotate(enabled: boolean, speed?: number): void {
    this.sceneManager?.setAutoRotate(enabled, speed);
  }
  
  /**
   * Set damping
   */
  public setDamping(enabled: boolean, factor?: number): void {
    this.sceneManager?.setDamping(enabled, factor);
  }
  
  /**
   * Set controls enabled state
   */
  public setControlsEnabled(config: { zoom?: boolean; pan?: boolean; rotate?: boolean }): void {
    this.sceneManager?.setControlsEnabled(config);
  }
  
  /**
   * Update ambient light intensity
   */
  public setAmbientIntensity(intensity: number): void {
    this.lightingManager.updateLight('ambient', { intensity });
    this.sceneManager?.requestRender();
  }
  
  /**
   * Get internal managers for advanced usage (dev tools)
   */
  public getInternals() {
    return {
      sceneManager: this.sceneManager,
      materialManager: this.materialManager,
      toggleManager: this.toggleManager,
      previewManager: this.previewManager,
      lightingManager: this.lightingManager,
      backgroundManager: this.backgroundManager,
    };
  }

  /**
   * Debug logging
   */
  private log(...args: unknown[]): void {
    if (this.options.debug) {
      console.log('[ProductVisualizer]', ...args);
    }
  }
  
  /**
   * Clean up all resources
   */
  public dispose(): void {
    // Remove keyboard handler
    if ((this as any)._keydownHandler) {
      window.removeEventListener('keydown', (this as any)._keydownHandler);
    }
    
    // Remove mouse handlers
    if ((this as any)._mouseHandlers && this.sceneManager) {
      const handlers = (this as any)._mouseHandlers;
      this.sceneManager.removeCanvasEventListener('mousedown', handlers.handleMouseDown);
      this.sceneManager.removeCanvasEventListener('mousemove', handlers.handleMouseMove);
      this.sceneManager.removeCanvasEventListener('mouseup', handlers.handleMouseUp);
      this.sceneManager.removeCanvasEventListener('mouseleave', handlers.handleMouseUp);
    }
    
    // Dispose managers
    this.materialManager.dispose();
    this.toggleManager.dispose();
    this.previewManager.dispose();
    this.lightingManager.dispose();
    this.selectionManager.dispose();
    this.viewSnapManager.dispose();
    this.backgroundManager.dispose();
    this.focusLockManager.dispose();
    
    // Dispose loaders
    this.modelLoader.dispose();
    this.textureLoader.dispose();
    this.hdriLoader.dispose();
    
    // Dispose resources
    this.resourceTracker.dispose();
    this.sceneManager?.dispose();
    this.uiController?.dispose();
    this.sdkBridge.dispose();
    
    this.initialized = false;
    this.log('Disposed');
  }
  
  /**
   * Check if visualizer is initialized
   */
  public get isInitialized(): boolean {
    return this.initialized;
  }
}

// Export types
export * from './types';

// Export individual modules for advanced usage
export { SceneManager } from './core';
export { ModelLoader, TextureLoader, HDRILoader } from './loaders';
export { 
  MaterialManager, 
  ToggleManager, 
  PreviewManager,
  LightingManager,
  SelectionManager,
  ViewSnapManager,
  BackgroundManager,
} from './managers';
export { GamifySdkBridge } from './sdk';
export { UIController } from './ui';
export { ResourceTracker } from './utils';
